import moment from "moment";
import momentTZ from "moment-timezone";
import { isEmpty, isNull } from "lodash";
import { TIME_FORMAT, DEFAULT_TIMEZONE } from "../constants";
import { toLocalDateInTimezone, toLocalTimeOfDay } from "../utils/datetime";

import firebase, { FS } from "./firebase";
import request from "../infra/request";
import { Appointment, AvailableTime, Timeslot } from "../types";
import { isPmsBookingEnabled } from "../infra/featureToggle";
import { fetchPractitionerAvailabilityFromApi } from "../api/availability";

const { MONTH, HOUR_AND_MIN, MONTH_WITH_DASH, DATE, DATE_WITH_DASH, DATE_WITH_TIME } = TIME_FORMAT;

export type Result = {
  result: any;
  error: boolean | null;
};

export const getScheduleByPractitionerId = async (practitionerId: string): Promise<Result> => {
  let result: string = "";
  try {
    const response = await request.get(`/admin/practitioners/${encodeURIComponent(practitionerId)}/schedule`);
    return { result: response.data.schedule, error: false };
  } catch {
    return { result, error: true };
  }
};

export const updateScheduleByPractitionerId = async (practitionerId: string, schedule: string): Promise<Result> => {
  try {
    await request.put(`/admin/practitioners/${encodeURIComponent(practitionerId)}/schedule`, { schedule });
  } catch {
    return { result: "", error: true };
  }
  return { result: "", error: false };
};

export const getPractitionerPreferences = async (): Promise<Array<object>> => {
  let preferences: Array<object> = [];
  try {
    const query = FS.query(FS.collection(firebase.modularFirestore(), "practitioner_preferences"));
    const snapshot = await FS.getDocs(query);
    return snapshot.docs.map((preferenceSnapshot) => ({
      ...preferenceSnapshot.data(),
      practitionerId: preferenceSnapshot.id,
    }));
  } catch (error) {
    console.log(error);
    return preferences;
  }
};

export const batchUpdatesPractitionerPreferences = async (batchUpdates: any) => {
  try {
    await request.put("/admin/practitioners/preferences", { preferences: batchUpdates });
  } catch (error) {
    console.log(error);
  }
};

export const getPractitionerEmailReviewingConfigs = async (): Promise<Result> => {
  let result: object = {};
  try {
    const response = await request.get("/admin/practitioners/email-review-config");
    const parctitionerEmailConfig: object | null = response.data.configs;
    if (isNull(parctitionerEmailConfig)) {
      return { result, error: null };
    }
    return { result: parctitionerEmailConfig, error: false };
  } catch (err) {
    console.log(err);
    return { result, error: true };
  }
};

export const updatePractitionerEmailReviewingConfigs = async (configs: object): Promise<Result> => {
  try {
    await request.put("/admin/practitioners/email-review-config", { configs });
    return { result: "", error: false };
  } catch (err) {
    return { result: "", error: true };
  }
};

export const getPractitionerEmailNotificationConfigs = async (): Promise<Result> => {
  let result: object = {};
  try {
    const response = await request.get("/admin/practitioners/email-notification-config");
    const parctitionerEmailConfig: object | null = response.data.configs;
    if (isNull(parctitionerEmailConfig)) {
      return { result, error: null };
    }
    return { result: parctitionerEmailConfig, error: false };
  } catch (err) {
    console.log(err);
    return { result, error: true };
  }
};

export const updatePractitionerEmailNotificationConfigs = async (configs: object): Promise<Result> => {
  try {
    await request.put("/admin/practitioners/email-notification-config", { configs });
    return { result: "", error: false };
  } catch (err) {
    return { result: "", error: true };
  }
};

// Settings → Patient Portal Booking Toggle: every practitioner_permissions document, keyed by
// practitioner id. Rejects when the request fails, as the page's own Firestore read did.
export const getPractitionerPermissions = async (): Promise<{ [practitionerId: string]: object }> => {
  const response = await request.get("/admin/practitioners/permissions");
  return response.data.permissions;
};

export const updatePractitionerPermissions = async (permissions: object): Promise<Result> => {
  try {
    await request.put("/admin/practitioners/permissions", { permissions });
    return { result: "", error: false };
  } catch (err) {
    return { result: "", error: true };
  }
};

// Settings → Practitioner Profile. Each rejects when the request fails, as the page's own
// Firestore calls did.
export const getProfileSpecialities = async (): Promise<Array<{ id: string; name: string }>> => {
  const response = await request.get("/admin/practitioners/specialities");
  return response.data.specialities;
};

// null when the practitioner has no profile yet.
export const getPractitionerProfile = async (practitionerId: string): Promise<any> => {
  const response = await request.get(`/admin/practitioners/${encodeURIComponent(practitionerId)}/profile`);
  return response.data.profile;
};

export const savePractitionerProfile = (practitionerId: string, profile: object) =>
  request.put(`/admin/practitioners/${encodeURIComponent(practitionerId)}/profile`, { profile });

type BookedTimeRange = { aptStart: string; aptEnd: string };
type TimeRange = { start: string; end: string };

const mergeSlotsWithOverlaps = (overlappedSlots: TimeRange[]): TimeRange[] => {
  const slots: TimeRange[] = [];
  const sorted = [...overlappedSlots].sort((a, b) => a.start.localeCompare(b.start) || a.end.localeCompare(b.end));

  sorted.forEach((slot) => {
    if (isEmpty(slots)) {
      slots.push(slot);
      return;
    }
    const lastSlot = slots[slots.length - 1];
    if (lastSlot.end >= slot.start) {
      slots[slots.length - 1] = {
        start: lastSlot.start,
        end: lastSlot.end >= slot.end ? lastSlot.end : slot.end,
      };
    } else {
      slots.push(slot);
    }
  });

  return slots;
};

const subtractBookedFromTimeslot = (timeslot: Timeslot, bookedRanges: BookedTimeRange[]): Timeslot[] => {
  const blocked = bookedRanges
    .map(({ aptStart, aptEnd }) => ({ start: aptStart, end: aptEnd }))
    .filter((slot) => !(slot.end <= timeslot.start || slot.start >= timeslot.end));

  const overlappedSlots = mergeSlotsWithOverlaps(blocked);
  if (isEmpty(overlappedSlots)) {
    return [timeslot];
  }

  const { selectedRoom } = timeslot;
  const availableTimeslots: Timeslot[] = [];

  if (overlappedSlots[0].start > timeslot.start) {
    availableTimeslots.push({ start: timeslot.start, end: overlappedSlots[0].start, selectedRoom });
  }

  let bufferEnd: string | null = null;
  overlappedSlots.forEach((slot) => {
    if (bufferEnd === null || bufferEnd >= slot.start) {
      bufferEnd = slot.end;
      return;
    }
    availableTimeslots.push({ start: bufferEnd, end: slot.start, selectedRoom });
    bufferEnd = slot.end;
  });

  if (bufferEnd !== null && bufferEnd < timeslot.end) {
    availableTimeslots.push({ start: bufferEnd, end: timeslot.end, selectedRoom });
  }

  return availableTimeslots;
};

export const generateTimeslots = (timeslot: Timeslot, appointments: BookedTimeRange[]) =>
  subtractBookedFromTimeslot(timeslot, appointments);

export const getPractitionerAvailability = async (
  practitionerId: any,
  month: string,
  appointmentType: string,
  requestingCaId: string
  // options: { locationId?: string; duration?: string } = {}
) => {
  if (isPmsBookingEnabled) {
    // The API buckets slots into days and labels them in this zone. Sending
    // the browser's guess meant a Melbourne coordinator saw a Queensland
    // specialist's slots an hour off, and near-midnight slots on the wrong day.
    const availability = await fetchPractitionerAvailabilityFromApi({
      specialistUserId: practitionerId,
      month,
      appointmentType,
      timezone: DEFAULT_TIMEZONE,
    });
    return availability;
  }

  const currentTimezone = DEFAULT_TIMEZONE;
  // The backend makes the queries this function used to make itself and returns only what the
  // calculation below needs; the appointments' start and end times, not their documents.
  const response = await request.get(`/admin/practitioners/${encodeURIComponent(practitionerId)}/availability-data`, {
    params: { month, excludeClientAppointmentId: requestingCaId },
  });
  const { availableTimes: availableTimeData, appointmentLength, bookedRanges } = response.data;
  // get current month practitioner's availableTimes with user's timezone

  let availableTimes: AvailableTime[] = [];
  availableTimeData.forEach((data: any) => {
    const { timezone: timeslotTimezone = "Australia/Melbourne", specialist_id, full_date, timeslots } = data;
    availableTimes = [
      ...availableTimes,
      {
        specialistId: specialist_id,
        date: moment(full_date, DATE).format(DATE_WITH_DASH),
        availableTimeslots: timeslots.map(({ start, end, selectedRoom }: Timeslot) => {
          const [startTime, endTime] = [
            momentTZ.tz(`${full_date}${start}`, DATE_WITH_TIME, timeslotTimezone).format(),
            momentTZ.tz(`${full_date}${end}`, DATE_WITH_TIME, timeslotTimezone).format(),
          ];
          const [startTimeWithTZ, endTimeWithTZ] = [
            momentTZ.tz(startTime, currentTimezone).format(HOUR_AND_MIN),
            momentTZ.tz(endTime, currentTimezone).format(HOUR_AND_MIN),
          ];
          return {
            start: startTimeWithTZ,
            end: endTimeWithTZ,
            selectedRoom,
          };
        }),
      },
    ];
  });

  // practitioner booked client appointments, then requested ones, without the one being edited
  const allApts: Appointment[] = bookedRanges.map(({ startDateTime, endDateTime }: Appointment) => ({
    startDateTime,
    endDateTime,
    practitionerId,
  }));

  const rightMonthFormat = moment(month, MONTH).format(MONTH_WITH_DASH);
  const appointmentsInMonth = allApts.filter(({ startDateTime, endDateTime }) => {
    const startMonth = toLocalDateInTimezone(startDateTime, currentTimezone)?.slice(0, 7);
    const endMonth = toLocalDateInTimezone(endDateTime, currentTimezone)?.slice(0, 7);
    return startMonth === rightMonthFormat && endMonth === rightMonthFormat;
  });

  // check booked time then remove it from availableTimes
  const result = availableTimes.map(({ specialistId, date, availableTimeslots }) => {
    const appointments = appointmentsInMonth
      .filter(({ startDateTime }) => toLocalDateInTimezone(startDateTime, currentTimezone) === date)
      .map(({ startDateTime, endDateTime }) => ({
        aptStart: toLocalTimeOfDay(startDateTime, currentTimezone),
        aptEnd: toLocalTimeOfDay(endDateTime, currentTimezone),
      }))
      .filter((appointment) => Boolean(appointment.aptStart && appointment.aptEnd));

    const finalAvailableTimeslots = availableTimeslots.reduce((availableTimeslots, timeslot) => {
      const timeslots = generateTimeslots(timeslot, appointments);
      return [...availableTimeslots, ...timeslots];
    }, []);

    return {
      originAvailableTimeslots: availableTimeslots,
      bookingTimeslots: appointments,
      specialistId,
      date,
      availableTimeslots: finalAvailableTimeslots,
    };
  });

  const satisfiedResult = result.map((item) => {
    const { availableTimeslots } = item;
    const aptLength = appointmentLength && appointmentLength[`${appointmentType.toLowerCase()}Time`];
    if (!aptLength || !Number(aptLength)) {
      return item;
    }

    const satisfiedAvailableTimeslots = availableTimeslots.reduce((satisfiedAvailableTimeslots, timeslot) => {
      const { start, end } = timeslot;
      const endTime = moment(start, HOUR_AND_MIN).add(Number(aptLength), "minutes").format(HOUR_AND_MIN);
      if (endTime <= end) {
        return [
          ...satisfiedAvailableTimeslots,
          {
            ...timeslot,
            isEnough: true,
          },
        ];
      }

      return [
        ...satisfiedAvailableTimeslots,
        {
          ...timeslot,
        },
      ];
    }, []);

    return {
      ...item,
      availableTimeslots: satisfiedAvailableTimeslots,
    };
  });

  return satisfiedResult.map((result) => {
    const { availableTimeslots, date } = result;
    // Tenant-zone calendar day, the same rule the PMS mapper applies: on the
    // browser clock a Brisbane tenant opened from Melbourne lost today's slots
    // after Melbourne midnight, and the midnight-vs-now comparison hid
    // same-day availability everywhere.
    const isValid =
      availableTimeslots.some(({ isEnough }: { isEnough: boolean }) => isEnough) &&
      momentTZ.tz(date, DATE_WITH_DASH, DEFAULT_TIMEZONE).isSameOrAfter(momentTZ().tz(DEFAULT_TIMEZONE), "day");
    return { ...result, isValid };
  });
};
